import { useEffect, useState } from 'react';
import { useRecordReleaseViews } from '@hub/api/mutations';
import { useCreditEventsModal } from '@hub/contexts/credit-events-modal';
import { useBreakpoints } from '@monorepo/nova-ui';
import { useWhatsNewModalLocalStorage, useWhatsNewModalSwiper } from './hooks';
import type { ReleaseData } from './types';
import { WhatsNewModalDesktop } from './whats-up-modal-desktop';
import { WhatsNewModalMobile } from './whats-up-modal-mobile';

export interface WhatsNewModalProps {
  releases: ReleaseData[];
  previewMode?: boolean;
}

export const WhatsNewModal = ({
  releases,
  previewMode = false,
}: WhatsNewModalProps) => {
  const [open, setOpen] = useState(false);
  const { mutate: recordViews } = useRecordReleaseViews();
  const { hasActiveCreditEvents } = useCreditEventsModal();

  const { isVisible, releaseIdsToShow, markAsSeen } =
    useWhatsNewModalLocalStorage(releases);

  const allReleaseIds = releases.map((r) => r.releaseId);

  const idsToShow = previewMode ? allReleaseIds : releaseIdsToShow;
  const idsSet = new Set(idsToShow);
  const filteredData = releases.filter((r) => idsSet.has(r.releaseId));

  const {
    activeIndex,
    slideCount,
    handleSlideChange,
    handleSwiper,
    handleBulletClick,
  } = useWhatsNewModalSwiper(filteredData.length);

  useEffect(() => {
    setOpen(previewMode ? true : isVisible && !hasActiveCreditEvents);
  }, [previewMode, isVisible, hasActiveCreditEvents]);

  const handleClose = () => {
    if (!previewMode) {
      markAsSeen(releaseIdsToShow);
      recordViews(releaseIdsToShow);
    }
    setOpen(false);
  };

  const { isMobileOrTablet } = useBreakpoints();

  if (releases.length === 0) return null;

  const sharedProps = {
    data: filteredData,
    open,
    setOpen,
    onClose: handleClose,
    activeIndex,
    slideCount,
    handleSlideChange,
    handleSwiper,
    handleBulletClick,
  };

  return isMobileOrTablet ? (
    <WhatsNewModalMobile {...sharedProps} />
  ) : (
    <WhatsNewModalDesktop {...sharedProps} />
  );
};
